import React, { useState } from 'react';

const LANGUAGES = ['English', 'Hindi', 'Arabic', 'Telugu', 'Tamil', 'Urdu', 'Spanish', 'French', 'German', 'Chinese', 'Portuguese', 'Other'];
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function StepBusiness({ data, updateData, onNext, onBack }) {
  const [prefilling, setPrefilling] = useState(false);
  const [prefilled, setPrefilled] = useState(false);
  const [errors, setErrors] = useState({});

  const handlePrefill = async () => {
    if (!data.businessName.trim() || data.businessName.length < 3) return;
    if (prefilled) return;

    setPrefilling(true);
    try {
      const response = await fetch(`${API_BASE}/onboarding/prefill`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          business_name: data.businessName,
          location: data.location || ''
        })
      });

      const result = await response.json();

      if (result.success && result.description) {
        updateData({
          businessDescription: result.description,
          languages: result.suggested_languages || ['English']
        });
        setPrefilled(true);
      }
    } catch (e) {
      console.error('Pre-fill failed:', e);
    } finally {
      setPrefilling(false);
    }
  };

  const toggleLanguage = (lang) => {
    const current = data.languages || [];
    if (current.includes(lang)) {
      if (current.length === 1) return;
      updateData({ languages: current.filter(l => l !== lang) });
    } else {
      updateData({ languages: [...current, lang] });
    }
  };

  const validate = () => {
    const e = {};
    if (!data.businessName.trim()) e.businessName = 'Business name is required';
    if (!data.businessDescription.trim()) e.businessDescription = 'Please describe what your business offers';
    return e;
  };

  const handleNext = () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    onNext();
  };

  return (
    <div className="ob-card">
      <h1 className="ob-title">About your business</h1>
      <p className="ob-subtitle">
        This helps our AI understand your calls and correctly identify what your customers are asking about.
      </p>

      {/* Business Name */}
      <div className="ob-field">
        <label className="ob-label">Business Name</label>
        <input
          className={`ob-input ${errors.businessName ? 'error' : ''}`}
          type="text"
          placeholder="Your business name"
          value={data.businessName}
          onChange={e => {
            updateData({ businessName: e.target.value });
            setPrefilled(false);
            setErrors(p => ({ ...p, businessName: '' }));
          }}
          onBlur={handlePrefill}
        />
        {errors.businessName && <p className="ob-error">{errors.businessName}</p>}
      </div>

      {/* Location */}
      <div className="ob-field">
        <label className="ob-label">Location (City, Country)</label>
        <input
          className="ob-input"
          type="text"
          placeholder="City, Country — e.g. New York, USA or London, UK"
          value={data.location}
          onChange={e => {
            updateData({ location: e.target.value });
            setPrefilled(false);
          }}
          onBlur={handlePrefill}
        />
        <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
          Used to personalise language suggestions and your AI setup
        </p>
      </div>

      {/* Business Description with AI pre-fill */}
      <div className="ob-field">
        <label className="ob-label">
          What does your business offer?
          {prefilling && (
            <span style={{ color: 'var(--cyan)', fontSize: 12, marginLeft: 8, fontWeight: 400 }}>
              <span className="ob-loading-dot" />
              <span className="ob-loading-dot" />
              <span className="ob-loading-dot" />
              {' '}AI is writing this for you...
            </span>
          )}
        </label>

        {prefilled && data.businessDescription && !prefilling && (
          <div className="ob-prefill-box">
            <span className="ob-prefill-badge">✨ AI suggested</span>
            <p className="ob-prefill-text">
              We pre-filled this based on your business name. Edit freely if needed.
            </p>
          </div>
        )}

        <textarea
          className={`ob-textarea ${errors.businessDescription ? 'error' : ''}`}
          placeholder="Describe your products and services — the more specific, the better our AI identifies them in calls.

e.g. We offer plumbing and electrical repair services for residential and commercial properties, including emergency call-outs, pipe repairs, and new installations."
          value={data.businessDescription}
          onChange={e => {
            updateData({ businessDescription: e.target.value });
            setErrors(p => ({ ...p, businessDescription: '' }));
          }}
          rows={4}
        />

        {!prefilled && data.businessName && !prefilling && (
          <button
            onClick={handlePrefill}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--cyan)',
              fontSize: 12,
              cursor: 'pointer',
              marginTop: 6,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            ✨ Let AI write this for me
          </button>
        )}

        {errors.businessDescription && <p className="ob-error">{errors.businessDescription}</p>}
      </div>

      {/* Languages */}
      <div className="ob-field">
        <label className="ob-label">Languages your customers speak</label>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
          Our AI will transcribe and translate calls in these languages automatically.
        </p>
        <div className="ob-lang-grid">
          {LANGUAGES.map(lang => (
            <div
              key={lang}
              className={`ob-lang ${(data.languages || []).includes(lang) ? 'selected' : ''}`}
              onClick={() => toggleLanguage(lang)}
            >
              <input
                type="checkbox"
                checked={(data.languages || []).includes(lang)}
                onChange={() => {}}
                style={{ pointerEvents: 'none' }}
              />
              {lang}
            </div>
          ))}
        </div>
      </div>

      <button className="ob-btn ob-btn-primary" onClick={handleNext}>
        Continue →
      </button>

      <button
        onClick={onBack}
        style={{
          background: 'none', border: 'none',
          color: 'var(--text-muted)', fontSize: 13,
          cursor: 'pointer', display: 'block',
          margin: '12px auto 0'
        }}
      >
        ← Back
      </button>
    </div>
  );
}
